/*global chrome*/
import { getCSRFToken } from "./django";

const Client = Object.freeze({
  APP: "app",
  BROWSER: "browser",
});

const CLIENT = Client.BROWSER;

const BASE_URL = `/_allauth/${CLIENT}/v1`;
const ACCEPT_JSON = {
  accept: "application/json",
};

export const AuthProcess = Object.freeze({
  LOGIN: "login",
  CONNECT: "connect",
});

// Some of these flows have not been implemented yet. Originally taken from: https://github.com/pennersr/django-allauth/blob/main/examples/react-spa/frontend/src/auth/routing.js
export const Flows = Object.freeze({
  VERIFY_EMAIL: "verify_email",
  LOGIN: "login",
  LOGIN_BY_CODE: "login_by_code",
  SIGNUP: "signup",
  PROVIDER_REDIRECT: "provider_redirect",
  PROVIDER_SIGNUP: "provider_signup",
});

export const URLs = Object.freeze({
  // Meta
  CONFIG: BASE_URL + "/config",

  // Account management
  CHANGE_PASSWORD: BASE_URL + "/account/password/change",
  EMAIL: BASE_URL + "/account/email",
  PROVIDERS: BASE_URL + "/account/providers",

  // Auth: Basics
  LOGIN: BASE_URL + "/auth/login",
  REQUEST_LOGIN_CODE: BASE_URL + "/auth/code/request",
  CONFIRM_LOGIN_CODE: BASE_URL + "/auth/code/confirm",
  SESSION: BASE_URL + "/auth/session",
  REQUEST_PASSWORD_RESET: BASE_URL + "/auth/password/request",
  RESET_PASSWORD: BASE_URL + "/auth/password/reset",
  SIGNUP: BASE_URL + "/auth/signup",
  SIGNUP_WEBAUTHN: BASE_URL + "/auth/webauthn/signup",
  VERIFY_EMAIL: BASE_URL + "/auth/email/verify",

  // Auth: Social
  PROVIDER_SIGNUP: BASE_URL + "/auth/provider/signup",
  REDIRECT_TO_PROVIDER: BASE_URL + "/auth/provider/redirect",
  PROVIDER_TOKEN: BASE_URL + "/auth/provider/token",

  // Auth: Sessions
  SESSIONS: BASE_URL + "/auth/sessions",
  EXTENSION_WS_TOKEN: "/api/ws-token",
  SUBSCRIPTION_STATUS: "/api/get-token-balance-and-subscription-status",
  SUBSCRIPTION_PROVIDER: "/api/get-subscription-provider",
  HAS_ACTIVE_SUBSCRIPTION_FOR_DELETION: "/api/has-active-subscription-for-deletion",
  DELETE_ACCOUNT: "/api/delete-account",
});

export const AuthenticatorType = Object.freeze({
  TOTP: "totp",
  RECOVERY_CODES: "recovery_codes",
  WEBAUTHN: "webauthn",
});

function postForm(action: string, data: Record<string, any>) {
  const f = document.createElement("form");
  f.method = "POST";
  f.action = action;

  for (const key in data) {
    const d = document.createElement("input");
    d.type = "hidden";
    d.name = key;
    d.value = data[key];
    f.appendChild(d);
  }
  document.body.appendChild(f);
  f.submit();
}

const tokenStorage = window.sessionStorage;

const runningInIframe = () => window.self !== window.top;

export function sendExtensionMessage(extensionId: string, request: any) {
  return new Promise((resolve, reject) => {
    if (chrome.runtime) {
      chrome.runtime.sendMessage(extensionId, request, (response: any) => {
        if (response) {
          // TODO: Some sources claim this should be if (response.success)... is that true?
          resolve(response);
        } else {
          reject(response);
        }
      });
    } else {
      reject("extension runtime not found");
    }
  });
}

export async function request(
  method: string,
  path: string,
  data?: any,
  headers?: Record<string, string>
) {
  const options: { method: string; headers: Record<string, string>; body?: string } = {
    method,
    headers: {
      ...ACCEPT_JSON,
      ...headers,
    },
  };
  // Don't pass along authentication related headers to the config endpoint.
  if (path !== URLs.CONFIG) {
    if (CLIENT === Client.BROWSER) {
      options.headers["X-CSRFToken"] = getCSRFToken() as string;
    } else if (CLIENT === Client.APP) {
      // IMPORTANT!: Do NOT use `Client.APP` in a browser context, as you will
      // be vulnerable to CSRF attacks. This logic is only here for
      // development/demonstration/testing purposes...
      options.headers["User-Agent"] = "django-allauth example app";
      const sessionToken = tokenStorage.getItem("sessionToken");
      if (sessionToken) {
        options.headers["X-Session-Token"] = sessionToken;
      }
    }
  }

  if (typeof data !== "undefined") {
    options.body = JSON.stringify(data);
    options.headers["Content-Type"] = "application/json";
  }

  // console.log(`CSRF Token: ${getCSRFToken()}`);
  // console.log(window.top.location)

  let msg;
  if (runningInIframe()) {
    // When running on iframe, we contact the web extension to route requests through it,
    // this way, we can keep session cookies samesite=Lax (instead of the more insecure samesite=None)
    // since the extension background does have host_permissions to read cookies, but the content script
    // doesn't get this access due to storage partitioning. That said, we still need to be careful on
    // how we validate/accept requests on the background script (before actually running them), since a bad
    // actor could send it messages to perform actions on behalf of the user (csrf-like)
    const webExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;
    console.log("Running inside iframe.....");

    // console.log("Web app will start contacting extension");

    const response = await sendExtensionMessage(webExtensionId, {
      message: "handle_fetch_request",
      path: path,
      options: options,
    });
    console.log("Web app received response from extension (on allauth.js)");
    console.log(response);
    msg = response;
    // console.log("Web app finished contacting extension");
  } else {
    // When running as top-site (in other words, NOT as an iframe) simply call fetch directly
    const resp = await fetch(path, options);
    msg = await resp.json();
  }

  if (
    [401, 410].includes(msg.status) ||
    (msg.status === 200 && msg.meta?.is_authenticated)
  ) {
    const event = new CustomEvent("allauth.auth.change", { detail: msg });
    document.dispatchEvent(event);
  }
  return msg;
}

export async function login(data: any) {
  return await request("POST", URLs.LOGIN, data);
}

export async function logout() {
  return await request("DELETE", URLs.SESSION);
}

export async function signUp(data: any) {
  return await request("POST", URLs.SIGNUP, data);
}

export async function signUpByPasskey(data: any) {
  return await request("POST", URLs.SIGNUP_WEBAUTHN, data);
}

export async function providerSignup(data: any) {
  return await request("POST", URLs.PROVIDER_SIGNUP, data);
}

export async function getProviderAccounts() {
  return await request("GET", URLs.PROVIDERS);
}

export async function disconnectProviderAccount(providerId: any, accountUid: any) {
  return await request("DELETE", URLs.PROVIDERS, {
    provider: providerId,
    account: accountUid,
  });
}

export async function requestPasswordReset(email: string) {
  return await request("POST", URLs.REQUEST_PASSWORD_RESET, { email });
}

export async function requestLoginCode(email: string) {
  return await request("POST", URLs.REQUEST_LOGIN_CODE, { email });
}

export async function confirmLoginCode(code: string) {
  return await request("POST", URLs.CONFIRM_LOGIN_CODE, { code });
}

export async function getEmailVerification(key: string) {
  return await request("GET", URLs.VERIFY_EMAIL, undefined, {
    "X-Email-Verification-Key": key,
  });
}

export async function getEmailAddresses() {
  return await request("GET", URLs.EMAIL);
}
export async function getSessions() {
  return await request("GET", URLs.SESSIONS);
}

export async function endSessions(ids: any) {
  return await request("DELETE", URLs.SESSIONS, { sessions: ids });
}

export async function getConfig() {
  return await request("GET", URLs.CONFIG);
}

export async function addEmail(email: string) {
  return await request("POST", URLs.EMAIL, { email });
}

export async function deleteEmail(email: string) {
  return await request("DELETE", URLs.EMAIL, { email });
}

export async function markEmailAsPrimary(email: string) {
  return await request("PATCH", URLs.EMAIL, { email, primary: true });
}

export async function requestEmailVerification(email: string) {
  return await request("PUT", URLs.EMAIL, { email });
}

export async function verifyEmail(key: string) {
  return await request("POST", URLs.VERIFY_EMAIL, { key });
}

export async function getPasswordReset(key: string) {
  return await request("GET", URLs.RESET_PASSWORD, undefined, {
    "X-Password-Reset-Key": key,
  });
}

export async function resetPassword(data: any) {
  return await request("POST", URLs.RESET_PASSWORD, data);
}

export async function changePassword(data: any) {
  return await request("POST", URLs.CHANGE_PASSWORD, data);
}

export async function getAuth() {
  return await request("GET", URLs.SESSION);
}

export async function getExtensionWsToken() {
  return await request("GET", URLs.EXTENSION_WS_TOKEN);
}

export async function getTokenBalanceAndSubscriptionStatus() {
  return await request("GET", URLs.SUBSCRIPTION_STATUS);
}

export async function getSubscriptionProvider() {
  return await request("GET", URLs.SUBSCRIPTION_PROVIDER);
}

export async function authenticateByToken(
  providerId: any,
  token: any,
  process = AuthProcess.LOGIN
) {
  return await request("POST", URLs.PROVIDER_TOKEN, {
    provider: providerId,
    token,
    process,
  });
}

export function redirectToProvider(
  providerId: any,
  callbackURL: any,
  process = AuthProcess.LOGIN
) {
  postForm(URLs.REDIRECT_TO_PROVIDER, {
    provider: providerId,
    process,
    callback_url: callbackURL,
    csrfmiddlewaretoken: getCSRFToken(),
  });
}
